import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronRightIcon, CircleIcon, Clock3Icon, MapPinIcon, SearchIcon, UserRoundIcon, XIcon } from 'lucide-react';
type Approval = {
  id: number;
  look: string;
  area: string;
  status: 'Approved' | 'Review' | 'Changes';
};
type Fitting = {
  id: number;
  time: string;
  model: string;
  look: string;
  done: boolean;
};
type Sample = {
  id: number;
  code: string;
  item: string;
  location: string;
  state: 'In atelier' | 'In transit' | 'At venue';
};
type Task = {
  id: number;
  task: string;
  owner: string;
  done: boolean;
};
const approvalSeed: Approval[] = [{
  id: 1,
  look: '02 — Nocturne',
  area: 'Styling',
  status: 'Approved'
}, {
  id: 2,
  look: '05 — Oxblood',
  area: 'Creative director',
  status: 'Changes'
}, {
  id: 3,
  look: '06 — Column I',
  area: 'Accessories',
  status: 'Review'
}, {
  id: 4,
  look: '08 — Finale',
  area: 'Casting',
  status: 'Approved'
}];
const fittingSeed: Fitting[] = [{
  id: 1,
  time: '09:30',
  model: 'Awar Odhiang',
  look: '01 / 07',
  done: true
}, {
  id: 2,
  time: '10:15',
  model: 'Mona Tougaard',
  look: '02 / 08',
  done: false
}, {
  id: 3,
  time: '11:00',
  model: 'Amar Akway',
  look: '03',
  done: false
}, {
  id: 4,
  time: '13:30',
  model: 'Loli Bahia',
  look: '04 / 06',
  done: false
}];
const sampleSeed: Sample[] = [{
  id: 1,
  code: 'FW-26-014',
  item: 'Bias silk cape',
  location: 'Rack A',
  state: 'At venue'
}, {
  id: 2,
  code: 'FW-26-021',
  item: 'Jet beaded column',
  location: 'Van 02',
  state: 'In transit'
}, {
  id: 3,
  code: 'FW-26-033',
  item: 'Patent opera coat',
  location: 'Atelier 4F',
  state: 'In atelier'
}, {
  id: 4,
  code: 'FW-26-041',
  item: 'Feather collar',
  location: 'Rack C',
  state: 'At venue'
}];
const taskSeed: Task[] = [{
  id: 1,
  task: 'Confirm runway floor polish',
  owner: 'N. Costa',
  done: true
}, {
  id: 2,
  task: 'Focus spot 04 at finale mark',
  owner: 'A. Kim',
  done: false
}, {
  id: 3,
  task: 'Place backstage garment rails',
  owner: 'L. Morel',
  done: false
}, {
  id: 4,
  task: 'Test press riser power',
  owner: 'J. Cole',
  done: true
}];
const cycle: Approval['status'][] = ['Review', 'Approved', 'Changes'];
const sampleStates: Sample['state'][] = ['In atelier', 'In transit', 'At venue'];
export function ProductionBoards() {
  const [approvals, setApprovals] = useState(approvalSeed);
  const [approvalFilter, setApprovalFilter] = useState('All');
  const [fittings, setFittings] = useState(fittingSeed);
  const [samples, setSamples] = useState(sampleSeed);
  const [tasks, setTasks] = useState(taskSeed);
  const [sampleQuery, setSampleQuery] = useState('');
  const [detail, setDetail] = useState<{
    title: string;
    body: string;
  } | null>(null);
  const filteredApprovals = useMemo(() => approvals.filter(item => approvalFilter === 'All' || item.status === approvalFilter), [approvalFilter, approvals]);
  const filteredSamples = useMemo(() => samples.filter(item => `${item.code} ${item.item} ${item.location}`.toLowerCase().includes(sampleQuery.toLowerCase())), [sampleQuery, samples]);
  function cycleApproval(id: number) {
    setApprovals(current => current.map(item => item.id === id ? {
      ...item,
      status: cycle[(cycle.indexOf(item.status) + 1) % cycle.length]
    } : item));
  }
  function cycleSample(id: number) {
    setSamples(current => current.map(item => item.id === id ? {
      ...item,
      state: sampleStates[(sampleStates.indexOf(item.state) + 1) % sampleStates.length]
    } : item));
  }
  return <>
      <div className="grid border-t border-black lg:grid-cols-2" data-id="element-1">
        <section id="approvals" className="py-8 lg:border-r lg:border-black lg:pr-8" aria-label="Look approvals" data-id="element-2">
          <BoardHeading eyebrow="02 / SIGN-OFF" title="LOOK APPROVALS" count={`${approvals.filter(a => a.status === 'Approved').length}/${approvals.length}`} data-id="element-3" />
          <div className="mb-5 flex flex-wrap gap-2" aria-label="Filter look approvals" data-id="element-4">
            {['All', 'Approved', 'Review', 'Changes'].map(filter => <button key={filter} type="button" onClick={() => setApprovalFilter(filter)} className={`filter-button ${approvalFilter === filter ? 'filter-button-active' : ''}`} data-id="element-5">
                {filter}
              </button>)}
          </div>
          <div className="divide-y divide-black border-y border-black" data-id="element-6">
            <AnimatePresence mode="popLayout" data-id="element-7">
              {filteredApprovals.map(item => <motion.button layout key={item.id} type="button" onClick={() => cycleApproval(item.id)} className="focus-ring grid w-full grid-cols-[1fr_auto] items-center gap-4 py-4 text-left" data-id="element-8">
                  <span data-id="element-9">
                    <strong className="block font-serif text-xl font-normal italic" data-id="element-10">
                      {item.look}
                    </strong>
                    <span className="utility-label" data-id="element-11">{item.area}</span>
                  </span>
                  <StatusMark label={item.status} data-id="element-12" />
                </motion.button>)}
            </AnimatePresence>
            {!filteredApprovals.length && <p className="py-8 text-center font-serif italic" data-id="element-13">
                Nothing awaiting this status.
              </p>}
          </div>
          <p className="mt-3 text-xs italic" data-id="element-14">
            Select a row to cycle its approval state.
          </p>
        </section>

        <section id="fittings" className="border-t border-black py-8 lg:border-t-0 lg:pl-8" aria-label="Model fittings" data-id="element-15">
          <BoardHeading eyebrow="03 / CASTING" title="MODEL FITTINGS" count={`${fittings.filter(f => f.done).length}/${fittings.length}`} data-id="element-16" />
          <div className="divide-y divide-black border-y border-black" data-id="element-17">
            {fittings.map(item => <div key={item.id} className="grid grid-cols-[60px_1fr_auto] items-center gap-3 py-4" data-id="element-18">
                <time className="font-serif text-xl" data-id="element-19">{item.time}</time>
                <button type="button" onClick={() => setDetail({
              title: item.model,
              body: `Fitting for look ${item.look}. Styling room B, with tailoring and accessories teams requested.`
            })} className="focus-ring text-left" data-id="element-20">
                  <strong className="block text-sm uppercase tracking-[.08em]" data-id="element-21">
                    {item.model}
                  </strong>
                  <span className="text-xs" data-id="element-22">Looks {item.look}</span>
                </button>
                <button type="button" onClick={() => setFittings(all => all.map(f => f.id === item.id ? {
              ...f,
              done: !f.done
            } : f))} className={`focus-ring grid h-9 w-9 place-items-center border border-black ${item.done ? 'bg-black text-white' : ''}`} aria-label={`${item.done ? 'Reopen' : 'Complete'} fitting for ${item.model}`} data-id="element-23">
                  {item.done ? <CheckIcon className="h-4 w-4" data-id="element-24" /> : <CircleIcon className="h-4 w-4" data-id="element-25" />}
                </button>
              </div>)}
          </div>
          <p className="handwritten mt-3 rotate-[-1deg] text-right text-2xl text-[#c51f2d]" data-id="element-26">
            Mona — check heel height!
          </p>
        </section>
      </div>

      <div className="grid border-t border-black lg:grid-cols-[1.15fr_.85fr]" data-id="element-27">
        <section id="samples" className="py-8 lg:border-r lg:border-black lg:pr-8" aria-label="Garment sample tracking" data-id="element-28">
          <BoardHeading eyebrow="04 / ATELIER" title="SAMPLE TRACKING" count={`${samples.length} pcs`} data-id="element-29" />
          <label className="mb-5 flex items-center gap-2 border-b border-black pb-2" data-id="element-30">
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-31" />
            <span className="sr-only" data-id="element-32">Search garment samples</span>
            <input value={sampleQuery} onChange={event => setSampleQuery(event.target.value)} placeholder="Search piece, code, location…" className="w-full bg-transparent text-sm outline-none placeholder:text-black/45" data-id="element-33" />
          </label>
          <div className="overflow-x-auto" data-id="element-34">
            <table className="w-full min-w-[560px] border-collapse text-left" data-id="element-35">
              <thead className="border-b border-black text-[10px] uppercase tracking-[.17em]" data-id="element-36">
                <tr data-id="element-37">
                  <th className="pb-3" data-id="element-38">Sample</th>
                  <th className="pb-3" data-id="element-39">Location</th>
                  <th className="pb-3" data-id="element-40">Movement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/30" data-id="element-41">
                {filteredSamples.map(item => <tr key={item.id} data-id="element-42">
                    <td className="py-4" data-id="element-43">
                      <strong className="block font-serif text-lg font-normal" data-id="element-44">
                        {item.item}
                      </strong>
                      <span className="text-[10px] font-bold tracking-[.15em]" data-id="element-45">
                        {item.code}
                      </span>
                    </td>
                    <td className="py-4 text-sm" data-id="element-46">{item.location}</td>
                    <td className="py-4" data-id="element-47">
                      <button type="button" onClick={() => cycleSample(item.id)} className="focus-ring inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[.1em]" data-id="element-48">
                        <MapPinIcon className="h-3.5 w-3.5 text-[#c51f2d]" data-id="element-49" />{' '}
                        {item.state}
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
            {!filteredSamples.length && <p className="py-8 text-center font-serif italic" data-id="element-50">
                No samples match that search.
              </p>}
          </div>
        </section>

        <section id="venue" className="border-t border-black py-8 lg:border-t-0 lg:pl-8" aria-label="Venue tasks" data-id="element-51">
          <BoardHeading eyebrow="05 / SITE" title="VENUE TASKS" count={`${tasks.filter(t => t.done).length}/${tasks.length}`} data-id="element-52" />
          <div className="space-y-1" data-id="element-53">
            {tasks.map(item => <label key={item.id} className="focus-within:ring-2 focus-within:ring-[#c51f2d] flex cursor-pointer items-start gap-4 border-b border-black py-4" data-id="element-54">
                <input type="checkbox" checked={item.done} onChange={() => setTasks(all => all.map(task => task.id === item.id ? {
              ...task,
              done: !task.done
            } : task))} className="peer sr-only" data-id="element-55" />
                <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center border border-black ${item.done ? 'bg-black text-white' : ''}`} data-id="element-56">
                  {item.done && <CheckIcon className="h-3.5 w-3.5" data-id="element-57" />}
                </span>
                <span className="flex-1" data-id="element-58">
                  <span className={`block font-serif text-lg ${item.done ? 'line-through opacity-50' : ''}`} data-id="element-59">
                    {item.task}
                  </span>
                  <span className="utility-label" data-id="element-60">{item.owner}</span>
                </span>
              </label>)}
          </div>
          <div className="mt-6 flex items-center gap-3 border-l-2 border-[#c51f2d] pl-4 text-sm" data-id="element-61">
            <Clock3Icon className="h-4 w-4 text-[#c51f2d]" data-id="element-62" />
            Venue handover today, 16:00 sharp.
          </div>
        </section>
      </div>

      <AnimatePresence data-id="element-63">
        {detail && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 grid place-items-center bg-black/55 p-4" onClick={() => setDetail(null)} data-id="element-64">
            <motion.div initial={{
          y: 18,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 18,
          opacity: 0
        }} role="dialog" aria-modal="true" className="w-full max-w-md bg-[#f4efe4] p-7" onClick={e => e.stopPropagation()} data-id="element-65">
              <button type="button" onClick={() => setDetail(null)} className="focus-ring float-right" data-id="element-66">
                <XIcon className="h-5 w-5" data-id="element-67" />
                <span className="sr-only" data-id="element-68">Close</span>
              </button>
              <UserRoundIcon className="mb-5 h-7 w-7 text-[#c51f2d]" data-id="element-69" />
              <h3 className="font-serif text-4xl italic" data-id="element-70">{detail.title}</h3>
              <p className="mt-5 border-t border-black pt-5 leading-relaxed" data-id="element-71">
                {detail.body}
              </p>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function BoardHeading({
  eyebrow,
  title,
  count
}: {
  eyebrow: string;
  title: string;
  count: string;
}) {
  return <div className="mb-6 flex items-start justify-between gap-4" data-id="element-72">
      <div data-id="element-73">
        <p className="utility-label mb-2" data-id="element-74">{eyebrow}</p>
        <h2 className="font-serif text-3xl leading-none sm:text-4xl" data-id="element-75">
          {title}
        </h2>
      </div>
      <span className="rounded-full border border-black px-3 py-1 text-xs" data-id="element-76">
        {count}
      </span>
    </div>;
}
function StatusMark({
  label
}: {
  label: string;
}) {
  return <span className={`inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.12em] ${label === 'Changes' ? 'text-[#c51f2d]' : ''}`} data-id="element-77">
      {label}
      <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-78" />
    </span>;
}